/**
 * iter282 — Cadrage avant lecture d'une carte grise.
 * On pivote la photo pour la remettre à l'endroit et on rogne sur le document
 * seul : l'arrière-plan (table, mains, autres papiers) n'est plus envoyé au
 * modèle, ce qui améliore nettement la lecture.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RotateCcw, RotateCw, Crop, ScanText, Loader2 } from "lucide-react";

const rotateDataUrl = (src, quartDeTour) => new Promise((resolve, reject) => {
  const img = new window.Image();
  img.onerror = reject;
  img.onload = () => {
    const pivote = quartDeTour % 2 === 1;
    const cv = document.createElement("canvas");
    cv.width = pivote ? img.height : img.width;
    cv.height = pivote ? img.width : img.height;
    const ctx = cv.getContext("2d");
    ctx.translate(cv.width / 2, cv.height / 2);
    ctx.rotate((quartDeTour * Math.PI) / 2);
    ctx.drawImage(img, -img.width / 2, -img.height / 2);
    resolve(cv.toDataURL("image/jpeg", 0.9));
  };
  img.src = src;
});

const cropDataUrl = (src, rect) => new Promise((resolve, reject) => {
  const img = new window.Image();
  img.onerror = reject;
  img.onload = () => {
    const x = Math.round(rect.x * img.width);
    const y = Math.round(rect.y * img.height);
    const w = Math.max(32, Math.round(rect.w * img.width));
    const h = Math.max(32, Math.round(rect.h * img.height));
    const cv = document.createElement("canvas");
    cv.width = w;
    cv.height = h;
    cv.getContext("2d").drawImage(img, x, y, w, h, 0, 0, w, h);
    resolve(cv.toDataURL("image/jpeg", 0.9));
  };
  img.src = src;
});

export default function CarteGriseAdjustDialog({ open, src, busy = false, onCancel, onValidate }) {
  const [apercu, setApercu] = useState(src);
  const [rect, setRect] = useState(null);     // fractions 0..1 de l'image affichée
  const [drag, setDrag] = useState(null);
  const [travail, setTravail] = useState(false);
  const zone = useRef(null);

  useEffect(() => { setApercu(src); setRect(null); }, [src]);

  const pivoter = async (sens) => {
    setTravail(true);
    try {
      setApercu(await rotateDataUrl(apercu, sens > 0 ? 1 : 3));
      setRect(null);
    } finally {
      setTravail(false);
    }
  };

  const positionRelative = useCallback((e) => {
    const box = zone.current.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - box.left) / box.width)),
      y: Math.min(1, Math.max(0, (e.clientY - box.top) / box.height)),
    };
  }, []);

  const debutSelection = (e) => {
    const p = positionRelative(e);
    setDrag(p);
    setRect({ x: p.x, y: p.y, w: 0, h: 0 });
  };

  const bougeSelection = (e) => {
    if (!drag) return;
    const p = positionRelative(e);
    setRect({
      x: Math.min(drag.x, p.x), y: Math.min(drag.y, p.y),
      w: Math.abs(p.x - drag.x), h: Math.abs(p.y - drag.y),
    });
  };

  const finSelection = () => {
    setDrag(null);
    setRect((r) => (r && r.w > 0.05 && r.h > 0.05 ? r : null));
  };

  const valider = async () => {
    const image = rect ? await cropDataUrl(apercu, rect) : apercu;
    onValidate(image);
  };

  const occupe = busy || travail;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-2xl" data-testid="cg-adjust-dialog">
        <DialogHeader>
          <DialogTitle>Cadrer la carte grise</DialogTitle>
          <DialogDescription>
            Remettez le document à l&apos;endroit, puis tracez un cadre autour de la carte grise
            pour exclure ce qu&apos;il y a autour. Les deux étapes sont facultatives.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => pivoter(-1)} disabled={occupe} data-testid="cg-adjust-rotate-left">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Pivoter à gauche
          </Button>
          <Button variant="outline" size="sm" onClick={() => pivoter(1)} disabled={occupe} data-testid="cg-adjust-rotate-right">
            <RotateCw className="w-4 h-4 mr-1.5" /> Pivoter à droite
          </Button>
          {rect && (
            <Button variant="ghost" size="sm" onClick={() => setRect(null)} data-testid="cg-adjust-reset-crop">
              Annuler le cadre
            </Button>
          )}
          <span className="text-xs text-gray-500 ml-auto inline-flex items-center gap-1">
            <Crop className="w-3.5 h-3.5" /> {rect ? "Cadre défini" : "Glissez sur l'image pour rogner"}
          </span>
        </div>

        <div className="flex justify-center bg-gray-100 rounded-lg p-2">
        <div
          ref={zone}
          onPointerDown={debutSelection}
          onPointerMove={bougeSelection}
          onPointerUp={finSelection}
          onPointerLeave={finSelection}
          className="relative inline-block select-none touch-none cursor-crosshair leading-none"
          data-testid="cg-adjust-zone"
        >
          <img src={apercu} alt="Carte grise" className="block max-h-[55vh] max-w-full pointer-events-none" />
          {rect && (
            <>
              <div className="absolute left-0 right-0 top-0 pointer-events-none" style={{ height: `${rect.y * 100}%`, backgroundColor: "rgba(0,0,0,0.5)" }} />
              <div className="absolute left-0 right-0 bottom-0 pointer-events-none" style={{ height: `${(1 - rect.y - rect.h) * 100}%`, backgroundColor: "rgba(0,0,0,0.5)" }} />
              <div className="absolute left-0 pointer-events-none" style={{ top: `${rect.y * 100}%`, height: `${rect.h * 100}%`, width: `${rect.x * 100}%`, backgroundColor: "rgba(0,0,0,0.5)" }} />
              <div className="absolute right-0 pointer-events-none" style={{ top: `${rect.y * 100}%`, height: `${rect.h * 100}%`, width: `${(1 - rect.x - rect.w) * 100}%`, backgroundColor: "rgba(0,0,0,0.5)" }} />
              <div
                className="absolute border-2 border-[#52a675] pointer-events-none"
                style={{
                  left: `${rect.x * 100}%`, top: `${rect.y * 100}%`,
                  width: `${rect.w * 100}%`, height: `${rect.h * 100}%`,
                }}
              />
            </>
          )}
        </div>
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} data-testid="cg-adjust-cancel">Annuler</Button>
          <Button
            onClick={valider}
            disabled={occupe}
            className="bg-[#52a675] hover:bg-[#3d8a5c]"
            data-testid="cg-adjust-validate"
          >
            {occupe ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanText className="w-4 h-4 mr-2" />}
            Lire la carte grise
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
